import type {
  AppDefinition,
  SessionRecord,
  SessionStatus,
} from '@web/api/types'
import { InfoPanel } from '@web/components/InfoPanel'
import { Icon } from '@web/components/ui/icons'

type Props = {
  app: AppDefinition
  archiveDisabled?: boolean
  model: string | null
  onArchive?: () => void
  onClose: () => void
  record: SessionRecord | null
  status: SessionStatus | 'unknown'
}

function formattedDate(timestamp: number | undefined) {
  if (!timestamp) return '—'
  const value = timestamp < 1_000_000_000_000 ? timestamp * 1_000 : timestamp
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(value)
}

/** Present the selected session's durable, user-facing record in a compact drawer. */
export function SessionDetailsPanel({
  app,
  archiveDisabled = false,
  model,
  onArchive,
  onClose,
  record,
  status,
}: Props) {
  const title = record?.title ?? 'Session'
  const provider = record?.config.provider ?? 'Agent'
  return (
    <aside className="session-details-panel" aria-label="Session details">
      <header className="session-details-heading">
        <div className="session-details-heading-copy">
          <p className="label">Session</p>
          <h2 title={title}>{title}</h2>
        </div>
        <button
          type="button"
          className="btn btn-icon"
          aria-label="Close session details"
          onClick={onClose}
        >
          <Icon name="close" size={16} />
        </button>
      </header>
      <div className="session-details-content">
        <dl className="session-details-list">
          <div>
            <dt>Status</dt>
            <dd className="capitalize">
              {status === 'unknown' ? 'Loading' : status}
            </dd>
          </div>
          <div>
            <dt>App</dt>
            <dd>{app.title}</dd>
          </div>
          <div>
            <dt>Agent</dt>
            <dd>{provider}</dd>
          </div>
          {model && (
            <div>
              <dt>Model</dt>
              <dd>{model}</dd>
            </div>
          )}
          <div>
            <dt>Created</dt>
            <dd>{formattedDate(record?.created_at)}</dd>
          </div>
          <div>
            <dt>Last updated</dt>
            <dd>{formattedDate(record?.updated_at)}</dd>
          </div>
          <div className="session-details-id">
            <dt>Session ID</dt>
            <dd>{record?.id ?? '—'}</dd>
          </div>
        </dl>
        <details className="session-details-advanced">
          <summary>Advanced details</summary>
          <InfoPanel embedded />
        </details>
      </div>
      {onArchive && (
        <footer className="session-details-actions">
          <button
            type="button"
            className="btn"
            disabled={archiveDisabled}
            onClick={onArchive}
          >
            <Icon name="trash" size={14} />
            Archive session
          </button>
        </footer>
      )}
    </aside>
  )
}
